import Spinner from '@components/spinner/Spinner';
import { useFetchShareTeamMember } from '@hooks/getQueries';
import {
  useLinkBasedShareInterview,
  useLinkBasedShareSurvey,
  useRevokeLinkBasedShareInterview,
  useRevokeLinkBasedShareSurvey,
} from '@hooks/postQueries';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

const frontendUrl = import.meta.env.VITE_FRONTEND_URL;

export default function PublicShare({
  study_link,
  open,
  handleClose,
  studyType,
  studyId: propStudyId,
}) {
  const [openSnackBar, setOpenSnackBar] = React.useState(false);
  // Use provided studyId from props if available, otherwise extract from study_link
  const studyId = propStudyId || study_link?.match(/[?&]study_id=([^&]*)/)?.[1] || '';
  const studyUrlType = studyType === 'Survey' ? 'dynamic-surveys' : 'qualitative-studies';
  const [generatedLink, setGeneratedLink] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  const returnUrl = `${frontendUrl}/accept-share-request/${studyId}/${studyUrlType}/`;

  const linkShareMutationSurvey = useLinkBasedShareSurvey();
  const revokeLinkMutationSurvey = useRevokeLinkBasedShareSurvey();
  const linkShareMutationInterview = useLinkBasedShareInterview();
  const revokeLinkMutationInterview = useRevokeLinkBasedShareInterview();

  const {
    data: shareTeamMembers = [],
    isPending,
    refetch,
  } = useFetchShareTeamMember(
    {
      studyId,
      studyUrlType,
      enabled: !!studyId && !!studyUrlType,
    },
    open,
  );

  // Find existing link-based share if any
  useEffect(() => {
    if (open && studyId && studyUrlType) {
      // Force invalidate the query to ensure fresh data
      queryClient.invalidateQueries(['team-share-member', studyId, studyUrlType]);
      refetch();
      // Don't reset generated link here - we'll handle link state based on API response
    }
  }, [open, studyId, studyUrlType, refetch, queryClient]);

  // Handle share team members data changes
  useEffect(() => {
    if (shareTeamMembers) {
      const linkShare = shareTeamMembers.find((member) => member.is_link_based);
      if (linkShare) {
        const shareUrl = `${returnUrl}?token=${linkShare.token}`;
        setGeneratedLink(shareUrl);
      }
    }
  }, [shareTeamMembers, returnUrl]);

  // Reset link when study changes (not when the component opens/closes)
  const prevStudyIdRef = React.useRef(studyId);
  useEffect(() => {
    if (prevStudyIdRef.current !== studyId) {
      // Study has changed, reset the link
      setGeneratedLink(null);
      prevStudyIdRef.current = studyId;
    }
  }, [studyId]);

  // Function to generate a new link
  const handleGenerateLink = () => {
    setIsLoading(true);

    if (studyType === 'Survey') {
      linkShareMutationSurvey.mutate(
        {
          surveyId: studyId,
          returnUrl,
        },
        {
          onSuccess: (data) => {
            toast.success('Share link created successfully!');
            // Immediately set the generated link without waiting for query invalidation
            setGeneratedLink(data?.share_url ?? null);
            queryClient.invalidateQueries(['team-share-member', studyId, studyUrlType]);
            setIsLoading(false);
          },
          onError: () => {
            toast.error('Failed to create share link.');
            setIsLoading(false);
          },
        },
      );
    } else {
      linkShareMutationInterview.mutate(
        {
          interviewId: studyId,
          returnUrl,
        },
        {
          onSuccess: (data) => {
            toast.success('Share link created successfully!');
            // Immediately set the generated link without waiting for query invalidation
            setGeneratedLink(data?.share_url ?? null);
            queryClient.invalidateQueries(['team-share-member', studyId, studyUrlType]);
            setIsLoading(false);
          },
          onError: () => {
            toast.error('Failed to create share link.');
            setIsLoading(false);
          },
        },
      );
    }
  };

  // Function to revoke the link
  const handleRevokeLink = () => {
    if (!generatedLink) return;

    setIsLoading(true);
    const token = generatedLink.split('token=')[1];

    if (studyType === 'Survey') {
      revokeLinkMutationSurvey.mutate(
        {
          surveyId: studyId,
          token: token,
        },
        {
          onSuccess: () => {
            toast.success('Share link has been revoked!');
            setGeneratedLink(null);
            queryClient.invalidateQueries(['team-share-member', studyId, studyUrlType]);
            setIsLoading(false);
          },
          onError: () => {
            toast.error('Failed to revoke share link.');
            setIsLoading(false);
          },
        },
      );
    } else {
      revokeLinkMutationInterview.mutate(
        {
          interviewId: studyId,
          token: token,
        },
        {
          onSuccess: () => {
            toast.success('Share link has been revoked!');
            setGeneratedLink(null);
            queryClient.invalidateQueries(['team-share-member', studyId, studyUrlType]);
            setIsLoading(false);
          },
          onError: () => {
            toast.error('Failed to revoke share link.');
            setIsLoading(false);
          },
        },
      );
    }
  };

  // Function to copy the link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      toast.success('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy text:', err);
      toast.error('Failed to copy to clipboard');
    }
  };

  // Debug info to help identify issues
  //console.log("PublicShare props:", { studyId, studyType, studyUrlType, open, shareTeamMembers });

  return (
    <>
      <Snackbar
        open={openSnackBar}
        autoHideDuration={1000}
        onClose={() => setOpenSnackBar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackBar(false)} severity="success" sx={{ width: '100%' }}>
          Text copied to clipboard!
        </Alert>
      </Snackbar>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth sx={{ p: 0 }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
          Share this study, outside your team
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent>
          <Box sx={{ py: 1 }}>
            {!studyId || !studyUrlType ? (
              <Typography align="center" py={3} color="error">
                Study information missing. Please try again.
              </Typography>
            ) : isPending || isLoading ? (
              <Spinner />
            ) : generatedLink ? (
              // Display when link is generated
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Share link:
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={generatedLink}
                    InputProps={{ readOnly: true }}
                  />

                  <IconButton color="primary" onClick={handleCopyLink} aria-label="Copy link">
                    <ContentCopyIcon />
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<LinkOffIcon />}
                    onClick={handleRevokeLink}
                    disabled={isLoading}
                  >
                    Revoke Link
                  </Button>

                  <Button variant="contained" onClick={handleClose}>
                    Done
                  </Button>
                </Box>
              </Box>
            ) : (
              // Display when no link is generated
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body1" gutterBottom>
                  No link has been generated yet. Generate a link to share with your participants.
                </Typography>

                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2 }}
                  onClick={handleGenerateLink}
                  disabled={isLoading}
                >
                  Generate Share Link
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
